// Period filters shared by reports, registrations, client pages and dashboard
// widgets: resolve a period key (+ offset for the ◀ ▶ arrows) to a date range,
// its comparison range and chart buckets.

import { i18n, t, registerMessages } from './i18n.svelte';
import { isoDate, isoWeek, parseDate } from './format';
import type { WorkSettings } from './types';

export type PeriodKey =
  | 'today' | 'yesterday' | 'week' | 'last_week' | 'month' | 'last_month' | 'quarter'
  | '7d' | '30d' | '3m' | '6m' | '12m' | 'year' | 'all' | 'current_period' | 'custom';

export interface PeriodRange {
  key: PeriodKey;
  /** Inclusive start (local midnight). */
  start: Date;
  /** Exclusive end (local midnight after the last day). */
  end: Date;
  days: number;
  offset: number;
  label: string;
}

export interface PeriodOptions {
  offset?: number;
  /** 0 = Sunday … 6 = Saturday (work settings week_start_day). */
  weekStart?: number;
  work?: Partial<WorkSettings> | null;
  from?: string | null;
  to?: string | null;
}

registerMessages({
  nl: {
    'period.today': 'Vandaag', 'period.yesterday': 'Gisteren', 'period.week': 'Deze week', 'period.last_week': 'Vorige week',
    'period.month': 'Deze maand', 'period.last_month': 'Vorige maand', 'period.quarter': 'Dit kwartaal', 'period.7d': 'Laatste 7 dagen',
    'period.30d': 'Laatste 30 dagen', 'period.3m': 'Laatste 3 maanden', 'period.6m': 'Laatste 6 maanden', 'period.12m': 'Laatste 12 maanden',
    'period.year': 'Dit jaar', 'period.all': 'Alles', 'period.current_period': 'Huidige periode', 'period.custom': 'Aangepast',
    'period.week_label': 'Week {week}, {year}', 'period.quarter_label': 'K{q} {year}', 'period.previous': 'Vorige periode', 'period.next': 'Volgende periode',
    'period.compared_to': 't.o.v. {label}', 'period.week_short': 'wk '
  },
  en: {
    'period.today': 'Today', 'period.yesterday': 'Yesterday', 'period.week': 'This week', 'period.last_week': 'Last week',
    'period.month': 'This month', 'period.last_month': 'Last month', 'period.quarter': 'This quarter', 'period.7d': 'Last 7 days',
    'period.30d': 'Last 30 days', 'period.3m': 'Last 3 months', 'period.6m': 'Last 6 months', 'period.12m': 'Last 12 months',
    'period.year': 'This year', 'period.all': 'All time', 'period.current_period': 'Current period', 'period.custom': 'Custom',
    'period.week_label': 'Week {week}, {year}', 'period.quarter_label': 'Q{q} {year}', 'period.previous': 'Previous period', 'period.next': 'Next period',
    'period.compared_to': 'vs {label}', 'period.week_short': 'W'
  }
});

export const PERIOD_KEYS: PeriodKey[] = ['today', 'week', 'month', 'quarter', 'year', 'current_period', '7d', '30d', '3m', '6m', '12m', 'all', 'custom'];

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function diffDays(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86400000);
}

export function startOfWeek(d: Date, weekStart = 1): Date {
  const day = d.getDay();
  const diff = (day - weekStart + 7) % 7;
  return addDays(startOfDay(d), -diff);
}

function fmtRange(start: Date, endExclusive: Date): string {
  const last = addDays(endExclusive, -1);
  const sameYear = start.getFullYear() === last.getFullYear();
  const a = start.toLocaleDateString(i18n.locale, { day: 'numeric', month: 'short', year: sameYear ? undefined : 'numeric' });
  const b = last.toLocaleDateString(i18n.locale, { day: 'numeric', month: 'short', year: 'numeric' });
  return start.getTime() === last.getTime() ? b : `${a} – ${b}`;
}

function billingPeriod(work: Partial<WorkSettings> | null | undefined, offset: number, now: Date): { start: Date; end: Date } {
  const type = work?.period_type || 'monthly';
  const duration = Math.max(1, Number(work?.period_duration) || 1);
  const anchor = parseDate(work?.period_start_date || null) || (type === 'weekly' ? startOfWeek(now) : new Date(now.getFullYear(), now.getMonth(), Math.max(1, Number(work?.period_start_day) || 1)));
  const step = (from: Date, n: number): Date => {
    if (type === 'weekly') return addDays(from, 7 * duration * n);
    if (type === 'quarterly') return new Date(from.getFullYear(), from.getMonth() + 3 * duration * n, from.getDate());
    return new Date(from.getFullYear(), from.getMonth() + duration * n, from.getDate());
  };
  // Walk from the anchor to the period that contains "now".
  let start = startOfDay(anchor);
  let guard = 0;
  while (step(start, 1) <= now && guard++ < 2000) start = step(start, 1);
  while (start > now && guard++ < 4000) start = step(start, -1);
  start = step(start, offset);
  return { start, end: step(start, 1) };
}

export function resolvePeriod(key: PeriodKey, opts: PeriodOptions = {}): PeriodRange {
  const offset = Math.min(0, opts.offset || 0);
  const weekStart = opts.weekStart ?? opts.work?.week_start_day ?? 1;
  const now = new Date();
  const today = startOfDay(now);
  let start: Date;
  let end: Date;
  let label: string;

  switch (key) {
    case 'today':
    case 'yesterday': {
      start = addDays(today, (key === 'yesterday' ? -1 : 0) + offset);
      end = addDays(start, 1);
      label = offset === 0 ? t(`period.${key}`) : start.toLocaleDateString(i18n.locale, { weekday: 'long', day: 'numeric', month: 'long' });
      break;
    }
    case 'week':
    case 'last_week': {
      start = addDays(startOfWeek(today, weekStart), 7 * ((key === 'last_week' ? -1 : 0) + offset));
      end = addDays(start, 7);
      label = t('period.week_label', { week: isoWeek(addDays(start, 3)), year: addDays(start, 3).getFullYear() });
      break;
    }
    case 'month':
    case 'last_month': {
      start = addMonths(today, (key === 'last_month' ? -1 : 0) + offset);
      end = addMonths(start, 1);
      label = start.toLocaleDateString(i18n.locale, { month: 'long', year: 'numeric' });
      break;
    }
    case 'quarter': {
      const q = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), q * 3 + 3 * offset, 1);
      end = addMonths(start, 3);
      label = t('period.quarter_label', { q: Math.floor(start.getMonth() / 3) + 1, year: start.getFullYear() });
      break;
    }
    case 'year': {
      start = new Date(today.getFullYear() + offset, 0, 1);
      end = new Date(start.getFullYear() + 1, 0, 1);
      label = String(start.getFullYear());
      break;
    }
    case '7d':
    case '30d': {
      const len = key === '7d' ? 7 : 30;
      end = addDays(today, 1 + len * offset);
      start = addDays(end, -len);
      label = offset === 0 ? t(`period.${key}`) : fmtRange(start, end);
      break;
    }
    case '3m':
    case '6m':
    case '12m': {
      const months = parseInt(key, 10);
      end = addDays(today, 1);
      end = new Date(end.getFullYear(), end.getMonth() + months * offset, end.getDate());
      start = new Date(end.getFullYear(), end.getMonth() - months, end.getDate());
      label = offset === 0 ? t(`period.${key}`) : fmtRange(start, end);
      break;
    }
    case 'current_period': {
      ({ start, end } = billingPeriod(opts.work, offset, now));
      label = fmtRange(start, end);
      break;
    }
    case 'custom': {
      start = parseDate(opts.from || null) || addMonths(today, 0);
      const to = parseDate(opts.to || null) || today;
      end = addDays(to < start ? start : to, 1);
      label = fmtRange(start, end);
      break;
    }
    case 'all':
    default: {
      start = new Date(2000, 0, 1);
      end = addDays(today, 3650);
      label = t('period.all');
      break;
    }
  }
  return { key, start, end, days: Math.max(1, diffDays(start, end)), offset, label };
}

/** The range of equal length right before `range` (for ▲▼ comparisons). */
export function previousRange(range: PeriodRange): PeriodRange {
  let start: Date;
  if (['month', 'last_month'].includes(range.key)) start = addMonths(range.start, -1);
  else if (range.key === 'quarter') start = addMonths(range.start, -3);
  else if (range.key === 'year') start = new Date(range.start.getFullYear() - 1, 0, 1);
  else start = addDays(range.start, -range.days);
  const end = range.start;
  return { ...range, start, end, days: diffDays(start, end), offset: range.offset - 1, label: fmtRange(start, end) };
}

export function canNavigate(key: PeriodKey): boolean {
  return key !== 'all' && key !== 'custom';
}

export function inRange(dateStr: string | null | undefined, range: Pick<PeriodRange, 'start' | 'end'>): boolean {
  const d = parseDate(dateStr || null);
  if (!d) return false;
  return d >= range.start && d < range.end;
}

export interface Bucket {
  start: Date;
  end: Date;
  label: string;
  /** Long label for tooltips. */
  title: string;
}

/** Chart buckets: daily ≤ 31 days, weekly ≤ 26 weeks, otherwise monthly. */
export function buckets(range: Pick<PeriodRange, 'start' | 'end' | 'days'>, weekStart = 1): Bucket[] {
  const out: Bucket[] = [];
  const locale = i18n.locale;
  let start = range.start;
  const end = range.end;
  if (range.days <= 31) {
    for (let d = start; d < end; d = addDays(d, 1)) {
      out.push({
        start: d,
        end: addDays(d, 1),
        label: range.days <= 7 ? d.toLocaleDateString(locale, { weekday: 'short' }) : String(d.getDate()),
        title: d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })
      });
    }
  } else if (range.days <= 190) {
    start = startOfWeek(start, weekStart);
    for (let d = start; d < end; d = addDays(d, 7)) {
      out.push({
        start: d,
        end: addDays(d, 7),
        label: `${t('period.week_short')}${isoWeek(addDays(d, 3))}`,
        title: fmtRange(d, addDays(d, 7))
      });
    }
  } else {
    start = new Date(start.getFullYear(), start.getMonth(), 1);
    for (let d = start; d < end; d = addMonths(d, 1)) {
      out.push({
        start: d,
        end: addMonths(d, 1),
        label: d.toLocaleDateString(locale, { month: 'short' }),
        title: d.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
      });
    }
  }
  return out;
}

/** Narrow an "all time" range to the span that actually has data. */
export function fitToData(range: PeriodRange, dates: string[]): PeriodRange {
  if (range.key !== 'all' || dates.length === 0) return range;
  let min = Infinity;
  let max = -Infinity;
  for (const s of dates) {
    const d = parseDate(s);
    if (!d) continue;
    min = Math.min(min, d.getTime());
    max = Math.max(max, d.getTime());
  }
  if (!Number.isFinite(min)) return range;
  const start = startOfDay(new Date(min));
  const end = addDays(startOfDay(new Date(Math.max(max, Date.now()))), 1);
  return { ...range, start, end, days: diffDays(start, end) };
}

export { isoDate, addDays, startOfDay };
